import React, { useEffect, useState } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import BaseWrapper from "../../components/Globo/Wrappers/BaseWrapper";
import MainContentWrapper from "../../components/Globo/Wrappers/MainContentWrapper";
import Header from "../../components/Globo/Header/Header";
import Pagination from "../../components/Globo/Pagination/Pagination";
import A11yGlobalStyles from "../../components/Globo/A11yGlobalStyles/A11yGlobalStyles";
import useDocumentTitle from "../../utils/a11y/useDocumentTitle";
import AreaLogo from "./AreaLogo";
import CultureHubFilter from "./Filters/CultureHubFilter";
import CultureHubSearchResults from "./CultureHubSearchResults";
import CultureHubModal from "./Modal/CultureHubModal";
import { useCultureHubSearch } from "../../hooks/CultureHub/useCultureHubSearch";
import { searchCultureHubProjects } from "../../services/api/cultureHub/cultureHub.api";
import { FONT_INTER } from "../../styles/fonts";

const DEFAULT_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = 16;
const PAGE_PARAM = "page";
const PAGE_SIZE_PARAM = "pageSize";

const readIntParam = (search, param, fallback) => {
  const value = Number(new URLSearchParams(search).get(param));
  return value > 0 ? value : fallback;
};

const searchCultureHub = (term, { cancelToken, pageIndex, pageSize }) =>
  searchCultureHubProjects({ title: term, pageIndex, pageSize, cancelToken });

const CultureHub = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [page, setPage] = useState(() =>
    readIntParam(location.search, PAGE_PARAM, DEFAULT_PAGE)
  );
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    readIntParam(location.search, PAGE_SIZE_PARAM, DEFAULT_ROWS_PER_PAGE)
  );

  const { term, searchedTerm, handleChange, handleSubmit, isSearching, results } =
    useCultureHubSearch(searchCultureHub, 700, {
      pageIndex: page - 1,
      pageSize: rowsPerPage,
    });

  const items = results?.items ?? [];
  const totalPages = results?.totalPages || 1;
  const totalItems = results?.totalCount ?? 0;
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    setPage(1);
  }, [term]);

  // keeps page/pageSize shareable via URL, only adding them when they differ from the defaults
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (page !== DEFAULT_PAGE) {
      params.set(PAGE_PARAM, String(page));
    } else {
      params.delete(PAGE_PARAM);
    }

    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) {
      params.set(PAGE_SIZE_PARAM, String(rowsPerPage));
    } else {
      params.delete(PAGE_SIZE_PARAM);
    }

    const nextSearch = params.toString();
    if (nextSearch !== location.search.replace(/^\?/, "")) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handlePageChange = (nextPage) => setPage(nextPage);
  const handleRowsPerPageChange = (nextRowsPerPage) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(1);
  };

  // gives screen reader users an accurate page name when the SPA navigates here
  useDocumentTitle("HUB de Cultura | Script");

  return (
    <Box sx={{ fontFamily: FONT_INTER }}>
      <A11yGlobalStyles />
      {/* !important beats base.scss's duplicated rule; both html and body need it, since base.scss
      sets a background on html too, and the browser uses html's color (not body's) to paint the
      canvas for the full scrollable page whenever content is taller than one viewport */}
      <GlobalStyles
        styles={{
          html: { backgroundColor: "#fff !important" },
          body: { backgroundColor: "#fff !important" },
        }}
      />
      <BaseWrapper>
        {/* BaseWrapper: main canvas area, where components like Header should be placed */}
        <Header titleComponent="p" />

        <MainContentWrapper maxWidth="1232px">
          {/* MainContentWrapper: area where the other main informations should be placed */}
          <Box
            sx={{
              minHeight: "calc(100vh - 64px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ px: 4, pt: 4 }}>
              <AreaLogo />

              <CultureHubFilter
                term={term}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />

              <CultureHubSearchResults
                results={items}
                loading={isSearching}
                term={searchedTerm.trim()}
                rowsPerPage={rowsPerPage}
              />
            </Box>

            {totalItems > 0 && (
              <Box sx={{ mt: "auto" }}>
                <Pagination
                  page={safePage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsLabel="projetos"
                  rowsPerPage={rowsPerPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Box>
            )}
          </Box>
        </MainContentWrapper>
      </BaseWrapper>

      <CultureHubModal />
    </Box>
  );
};

export default CultureHub;
